import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AppLayout,
  AppLayoutProps,
  Box,
  Button,
  ContentLayout,
  Container,
  Flashbar,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-react";
import {
  useOmsOwnedExport,
  useOmsOwnedSource,
} from "../../hooks/useOmsOwnedExport";
import OMSOwnedExportResult from "./OMSOwnedExportResult";
import useOmsPresets from "../../hooks/useOmsPresets";
import { OMSPresetManager } from "./OMSPresetEditor";
import OMSPresetCopy from "./OMSPresetCopy";
import { useOmsNavigationGuard } from "./OMSNavigationGuard";
import { OmsProduct, Stage } from "../../lusca-search-api/generated-src";
import { useOmsContext, useOmsContexts } from "../../hooks/useOmsContexts";
import {
  OmsContextIdentityContext,
  omsContextPath,
  useOmsIdentity,
} from "../../hooks/omsContextIdentity";
import useOmsReport from "../../hooks/useOmsReport";
import {
  clearOmsDraft,
  hasPendingOmsRequest,
  readOmsRecoveryDraft,
  writeOmsDraft,
} from "../../hooks/omsDraftStorage";
import { OmsFilterDraft } from "../../hooks/useOmsFilterCriteria";
import { latestOmsRecovery } from "../../hooks/useOmsExport";
import OMSExportWorkspace from "./OMSExportWorkspace";
import OMSReportSchedule from "./OMSReportSchedule";
import OMSPersonalExportLink from "./OMSPersonalExportLink";
import { DashboardType, QuickSightDashboard } from "./QuicksightDashboard";
import PageHeader from "../PageHeader";
import TrackStarsLaunch from "../common/TrackStarsLaunch";
import { StageContext } from "../../contexes/OrderIdContext";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import "./omsReportDownload.scss";
import { OMSHelpContext, OMSHelpPanel, OMSHelpTopic } from "./OMSContextHelp";

const products = [
  { value: OmsProduct.Channels, label: "PV Channels", gl: 613 },
  { value: OmsProduct.Addon, label: "PV Addon", gl: 796 },
  { value: OmsProduct.Tvod, label: "PV TVOD", gl: 318 },
];

function route(path: string) {
  const match = path.match(
    /^\/dashboard\/oms(?:\/(channels|addon|tvod)(?:\/account\/(\d{5}))?(?:\/(export|presets)(?:\/([^/]+)\/(edit|copy))?)?)?\/?$/
  );
  return match && (!match[4] || match[3] === "presets")
    ? {
        product: match[1] as OmsProduct | undefined,
        account: match[2],
        view:
          match[3] === "presets"
            ? match[5]
              ? match[5]
              : "manage"
            : match[3] === "export"
            ? "export"
            : "dashboard",
        presetId: match[4],
      }
    : undefined;
}

type OMSContextContentProps = {
  product: OmsProduct;
  label: string;
  account?: string;
  context: NonNullable<ReturnType<typeof useOmsContext>["data"]>;
  view: string;
  presetId?: string;
  search: string;
  navigate: (path: string, replace?: boolean) => void;
  onDirty: (value: boolean) => void;
  onNotify: (message: string) => void;
  onBeginExport: () => void;
};

function OMSContextDashboard({
  product,
  account,
  context,
  view,
  search,
  navigate,
  setRefreshHandler,
}: Pick<
  OMSContextContentProps,
  "product" | "account" | "context" | "view" | "search" | "navigate"
> & {
  setRefreshHandler: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
}) {
  const [visited, setVisited] = useState(view === "dashboard");
  const [params, setParams] = useState(() => new URLSearchParams(search));
  const [, setShareHandler] = useState<(() => Promise<string>) | null>(null);
  useEffect(() => {
    if (view === "dashboard") {
      setVisited(true);
      setParams(new URLSearchParams(search));
    }
  }, [view, search]);
  const available = account
    ? context.capabilities.dashboardAvailable
    : !context.unavailableReason;
  if (!available || !visited) return null;
  return (
    <section
      className="oms-context-dashboard"
      aria-label="Embedded OMS dashboard"
      hidden={view !== "dashboard"}
    >
      <QuickSightDashboard
        stage={Stage.Prod}
        dashboardType={`pv_${product}` as DashboardType}
        financialAccount={account}
        searchParams={params}
        setSearchParams={(next) => {
          setParams(next);
          const query = next.toString();
          navigate(
            `${omsContextPath(product, account)}${query ? `?${query}` : ""}`
          );
        }}
        setShareHandler={setShareHandler}
        setRefreshHandler={setRefreshHandler}
      />
    </section>
  );
}

function OMSContextWorkflow({
  product,
  label,
  account,
  context,
  view,
  presetId,
  search,
  navigate,
  onDirty,
  onNotify,
  onBeginExport,
  refreshDashboard,
}: OMSContextContentProps & {
  refreshDashboard: (() => Promise<void>) | null;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const { namespace } = useOmsIdentity(product);
  const base = omsContextPath(product, account);
  const legacy = !account;
  const admission = legacy || context.capabilities.newExportControls;
  const report = useOmsReport(
    product,
    legacy || context.capabilities.newAdmissions
  );
  const [draft, setDraft] = useState<OmsFilterDraft | undefined>(() =>
    readOmsRecoveryDraft(product, namespace)
  );
  const [source, setSource] = useState(() => draft?.report);
  const recovery = latestOmsRecovery(product, namespace);
  const saveDraft = useCallback(
    (next: OmsFilterDraft) => {
      writeOmsDraft(product, next, namespace);
      setDraft(next);
    },
    [product, namespace]
  );
  useEffect(() => {
    if (
      legacy &&
      view === "dashboard" &&
      context.legacyAliasEligible &&
      report.data?.financialAccount === "13051"
    ) {
      navigate(omsContextPath(product, "13051") + search, true);
    }
  }, [
    legacy,
    view,
    context.legacyAliasEligible,
    report.data?.financialAccount,
    product,
    search,
    navigate,
  ]);
  useEffect(() => {
    if (view === "dashboard") {
      if (draft && !hasPendingOmsRequest(draft)) {
        clearOmsDraft(product, namespace);
        setDraft(undefined);
      }
      setSource(undefined);
    }
  }, [view, draft, product, namespace]);
  const jobId = new URLSearchParams(search).get("job");
  const owned = useOmsOwnedExport(product, jobId);
  const original = useOmsOwnedSource(product, jobId, Boolean(owned.data));
  const presets = useOmsPresets(product, view === "manage");
  const lastDraft = useRef(draft);
  lastDraft.current = draft;
  useEffect(
    () => () => {
      if (!hasPendingOmsRequest(lastDraft.current))
        clearOmsDraft(product, namespace);
    },
    [product, namespace]
  );
  useEffect(() => {
    if (view !== "dashboard" && !source && !jobId)
      setSource(draft?.report || report.data);
    if (
      jobId &&
      original.data &&
      source?.snapshotId !== original.data.snapshotId
    )
      setSource(original.data);
  }, [view, source, jobId, draft?.report, report.data, original.data]);
  const onWorkflowNavigate = useCallback(
    (next: "export" | "manage" | "edit", id?: string, review = false) => {
      const path =
        next === "manage"
          ? `${base}/presets`
          : next === "edit"
          ? `${base}/presets/${encodeURIComponent(id || "")}/edit`
          : `${base}/export${review ? "?view=review" : ""}`;
      navigate(path);
    },
    [base, navigate]
  );
  const onJob = useCallback(
    (id: string | null) => {
      if (view === "export" && new URLSearchParams(search).get("job") !== id)
        navigate(`${base}/export${id ? `?job=${encodeURIComponent(id)}` : ""}`);
    },
    [base, navigate, search, view]
  );
  const notice =
    context.unavailableReason ||
    (!legacy && !context.capabilities.newAdmissions
      ? "This financial account is not enabled for new reports."
      : undefined);
  const copyPath = (id: string) =>
    `${base}/presets/${encodeURIComponent(id)}/copy${
      jobId ? `?job=${encodeURIComponent(jobId)}` : ""
    }`;
  if (view === "copy" && presetId)
    return (
      <div className="oms-context-workflow">
        <OMSPresetCopy
          key={`${product}:${account || "legacy"}:${presetId}`}
          product={product}
          account={account}
          presetId={presetId}
          destinationKey={new URLSearchParams(search).get("to") || undefined}
          onDestination={(key) => {
            const next = new URLSearchParams(search);
            next.set("to", key);
            navigate(
              `${base}/presets/${encodeURIComponent(
                presetId
              )}/copy?${next.toString()}`
            );
          }}
          onBack={() =>
            navigate(
              `${base}/presets${
                jobId ? `?job=${encodeURIComponent(jobId)}` : ""
              }`
            )
          }
          onDirty={onDirty}
          onOpenDestination={(targetProduct, targetAccount) =>
            navigate(`${omsContextPath(targetProduct, targetAccount)}/presets`)
          }
        />
      </div>
    );
  if (view !== "dashboard") {
    const pinned = jobId
      ? owned.data &&
        (source?.snapshotId === owned.data.snapshotId ? source : original.data)
      : source;
    return (
      <div className="oms-context-workflow">
        <SpaceBetween size="l">
          {notice && (
            <Alert type="info" header="New exports unavailable">
              {notice} Accepted exports and saved presets remain recoverable.
            </Alert>
          )}
          {pinned ? (
            <OMSExportWorkspace
              product={product}
              productLabel={`${label}${
                pinned.financialAccount
                  ? ` (Account ${pinned.financialAccount})`
                  : ""
              }`}
              report={pinned}
              availableReport={report.data || pinned}
              draft={draft}
              initialJob={
                owned.data
                  ? { jobId: owned.data.jobId, mode: owned.data.mode }
                  : undefined
              }
              resumeExport={Boolean(jobId || (draft && !draft.editing))}
              onSaveDraft={saveDraft}
              onClose={(next) => {
                saveDraft(next);
                navigate(base);
              }}
              page={{
                view: view as "export" | "manage" | "edit",
                presetId,
                review: new URLSearchParams(search).get("view") === "review",
                admission: admission && !notice,
                onNavigate: onWorkflowNavigate,
                onDirty,
                onJob,
                onNotify,
                onBeginExport,
                onCopy: (id) => navigate(copyPath(id)),
              }}
            />
          ) : jobId ? (
            <OMSOwnedExportResult product={product} jobId={jobId} />
          ) : view === "manage" ? (
            <OMSPresetManager
              confirmInModal
              readOnly
              state={presets}
              onEdit={(id) => onWorkflowNavigate("edit", id)}
              onCopy={(id) => navigate(copyPath(id))}
              onDeleted={() => onNotify("Preset deleted.")}
              onRenamed={(preset) =>
                onNotify(`Renamed preset to ${preset.name}.`)
              }
            />
          ) : (
            <Alert
              type={report.isLoading ? "info" : "warning"}
              header={
                report.isLoading
                  ? "Loading report"
                  : "No completed report available"
              }
            >
              {report.isLoading
                ? "Loading export settings…"
                : "Your accepted export can still be opened from Return to export on the dashboard."}
            </Alert>
          )}
        </SpaceBetween>
      </div>
    );
  }
  return (
    <SpaceBetween size="l">
      <section className="oms-report-card" aria-label={`${label} report`}>
        <Container
          className="oms-workflow-section"
          header={
            <Header
              variant="h2"
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    iconName="refresh"
                    ariaLabel={
                      refreshDashboard
                        ? "Refresh report and dashboard"
                        : "Check for a newer report"
                    }
                    loading={report.isFetching || refreshing}
                    onClick={() => {
                      setRefreshing(true);
                      setRefreshError(false);
                      Promise.all([
                        report.refetch({ throwOnError: true }),
                        refreshDashboard?.(),
                      ])
                        .catch(() => setRefreshError(true))
                        .finally(() => setRefreshing(false));
                    }}
                  >
                    Refresh
                  </Button>
                  {admission && !notice && (
                    <Button
                      variant="primary"
                      iconName="download"
                      disabled={!report.data || report.isError}
                      onClick={() => navigate(`${base}/export`)}
                    >
                      Export report
                    </Button>
                  )}
                </SpaceBetween>
              }
            >
              {label} report
            </Header>
          }
        >
          <SpaceBetween size="m">
            {refreshError && (
              <Alert type="error" header="Refresh incomplete">
                The report or dashboard could not be refreshed. Try again.
              </Alert>
            )}
            {notice ? (
              <Alert type="info">{notice}</Alert>
            ) : report.isLoading ? (
              <div role="status" className="oms-report-download__status">
                <Spinner />
                <span>Loading report details…</span>
              </div>
            ) : report.isError || !report.data ? (
              <Alert type="info" header="Report unavailable">
                No completed report is available. Your accepted export is
                unaffected.
              </Alert>
            ) : (
              <OMSReportSchedule
                product={product}
                sourceRefreshedAt={report.data.sourceRefreshedAt}
                dailySchedule={
                  context.scheduleApproved ? context.dailyScheduleUtc : null
                }
              />
            )}
            {legacy && report.data && !report.data.financialAccount && (
              <Box color="text-body-secondary">
                The financial account of this report is not verified.
              </Box>
            )}
            {(recovery?.report || report.data) && (
              <OMSPersonalExportLink
                report={(recovery?.report || report.data)!}
                open={false}
                onOpen={() =>
                  navigate(
                    `${base}/export?job=${encodeURIComponent(
                      recovery?.jobId || ""
                    )}`
                  )
                }
              />
            )}
          </SpaceBetween>
        </Container>
      </section>
    </SpaceBetween>
  );
}

function OMSContextContent(props: OMSContextContentProps) {
  const [refreshDashboard, setRefreshHandler] = useState<
    (() => Promise<void>) | null
  >(null);
  return (
    <>
      <OMSContextWorkflow {...props} refreshDashboard={refreshDashboard} />
      <OMSContextDashboard {...props} setRefreshHandler={setRefreshHandler} />
    </>
  );
}

export default function OMSContextShell() {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const updateScrollbarWidth = () => {
      const probe = document.createElement("div");
      probe.style.cssText =
        "position:absolute;width:100px;height:100px;overflow:scroll;visibility:hidden;top:-10000px";
      document.body.appendChild(probe);
      root.style.setProperty(
        "--oms-scrollbar-width",
        `${Math.max(0, probe.offsetWidth - probe.clientWidth)}px`
      );
      probe.remove();
    };
    root.classList.add("oms-context-active");
    updateScrollbarWidth();
    window.addEventListener("resize", updateScrollbarWidth);
    return () => {
      window.removeEventListener("resize", updateScrollbarWidth);
      root.classList.remove("oms-context-active");
      root.style.removeProperty("--oms-scrollbar-width");
    };
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const committed = location;
  const setDirty = useOmsNavigationGuard();
  const current = route(committed.pathname);
  const detail = useOmsContext(current?.product, current?.account);
  const discovered = useOmsContexts();
  const context = detail.data;
  const product = products.find((item) => item.value === current?.product);
  const accountLabel = current?.account ? ` (Account ${current.account})` : "";
  const heading = `${product?.label || "PV"} OMS${accountLabel}`;
  const viewTitles: Record<string, string> = {
    export:
      new URLSearchParams(committed.search).get("view") === "review"
        ? "Review export"
        : "Export report",
    manage: "Saved presets",
    edit: "Edit preset",
    copy: "Reuse preset",
  };
  const viewTitle = viewTitles[current?.view || "dashboard"];
  useDocumentTitle(viewTitle ? `${viewTitle} | ${heading}` : heading);
  const headingRoot = useRef<HTMLDivElement>(null);
  const layout = useRef<AppLayoutProps.Ref>(null);
  const helpOrigin = useRef<Element | null>(null);
  const [help, setHelp] = useState<OMSHelpTopic>();
  const openHelp = useCallback(
    (topic: OMSHelpTopic, origin: Element | null) => {
      helpOrigin.current = origin;
      setHelp(topic);
    },
    []
  );
  useEffect(() => {
    if (help) layout.current?.focusToolsClose();
  }, [help]);
  useEffect(() => setHelp(undefined), [current?.product, current?.account]);
  const previousPath = useRef(committed.pathname);
  const noticeContext =
    context?.canonicalNamespace ||
    `${current?.product || ""}:${current?.account || ""}`;
  const [notification, setNotification] = useState<{
    message: string;
    namespace: string;
  }>();
  const [notificationHovered, setNotificationHovered] = useState(false);
  const [notificationFocused, setNotificationFocused] = useState(false);
  const clearNotification = useCallback(() => {
    setNotification(undefined);
    setNotificationHovered(false);
    setNotificationFocused(false);
  }, []);
  const onNotify = useCallback(
    (message: string) => {
      setNotification({ message, namespace: noticeContext });
    },
    [noticeContext]
  );
  useEffect(() => {
    clearNotification();
  }, [current?.product, current?.account, clearNotification]);
  useEffect(() => {
    if (!notification || notificationHovered || notificationFocused)
      return undefined;
    const timer = window.setTimeout(clearNotification, 8000);
    return () => window.clearTimeout(timer);
  }, [
    notification,
    notificationHovered,
    notificationFocused,
    clearNotification,
  ]);
  useEffect(() => {
    if (previousPath.current === committed.pathname) return undefined;
    previousPath.current = committed.pathname;
    const timer = window.setTimeout(() => {
      const title = headingRoot.current?.querySelector("h1");
      title?.setAttribute("tabindex", "-1");
      title?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [committed.pathname]);
  const navigateTo = useCallback(
    (path: string, replace = false) => navigate(path, { replace }),
    [navigate]
  );
  const contextIdentity = useMemo(
    () => ({
      namespace: context?.canonicalNamespace || current?.product || "",
      financialAccount: current?.account,
    }),
    [context?.canonicalNamespace, current?.product, current?.account]
  );
  return (
    <OMSHelpContext.Provider value={openHelp}>
      <StageContext.Provider value={Stage.Prod}>
        <PageHeader />
        <AppLayout
          className="oms-context-page"
          ref={layout}
          navigationHide
          toolsHide={!help}
          toolsOpen={Boolean(help)}
          tools={help && <OMSHelpPanel topic={help} />}
          ariaLabels={{
            tools: "OMS help",
            toolsClose: "Close OMS help",
            toolsToggle: "Open OMS help",
          }}
          onToolsChange={({ detail: tools }) => {
            if (!tools.open) {
              setHelp(undefined);
              window.setTimeout(() => {
                if (
                  helpOrigin.current instanceof HTMLElement &&
                  helpOrigin.current.isConnected
                )
                  helpOrigin.current.focus();
              }, 0);
            }
          }}
          headerSelector="#h"
          maxContentWidth={1520}
          notifications={
            <div
              onMouseEnter={() => setNotificationHovered(true)}
              onMouseLeave={() => setNotificationHovered(false)}
              onFocus={() => setNotificationFocused(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget))
                  setNotificationFocused(false);
              }}
            >
              <Flashbar
                items={
                  notification?.namespace === noticeContext
                    ? [
                        {
                          id: "oms-preset-confirmation",
                          type: "success",
                          content: notification.message,
                          dismissible: true,
                          dismissLabel: "Dismiss preset confirmation",
                          ariaRole: "status",
                          onDismiss: clearNotification,
                        },
                      ]
                    : []
                }
              />
            </div>
          }
          content={
            <ContentLayout
              defaultPadding
              header={
                <SpaceBetween size="m">
                  <div className="oms-context-header">
                    <div ref={headingRoot}>
                      <Header variant="h1">{heading}</Header>
                    </div>
                    <div className="oms-context-header__assistant">
                      <TrackStarsLaunch product="oms" />
                    </div>
                    <div className="oms-context-header__controls">
                      <div className="oms-context-header__selector">
                        <FormField label="Financial account">
                          <Select
                            expandToViewport
                            ariaLabel="Financial account"
                            filteringType="auto"
                            filteringPlaceholder="Find product, GL or account"
                            filteringAriaLabel="Find a financial account"
                            filteringClearAriaLabel="Clear account search"
                            selectedAriaLabel="Selected"
                            noMatch="No matching accounts"
                            renderHighlightedAriaLive={(option, group) =>
                              [group?.label, option.label]
                                .filter(Boolean)
                                .join(", ")
                            }
                            selectedOption={
                              product && current?.account
                                ? {
                                    value: `${product.value}:${current.account}`,
                                    label: `${product.label} (${current.account})`,
                                  }
                                : null
                            }
                            options={products.map((item) => ({
                              label: `${item.label} (GL ${item.gl})`,
                              options: (discovered.data?.contexts || [])
                                .filter((c) => c.product === item.value)
                                .map((c) => ({
                                  value: `${c.product}:${c.financialAccount}`,
                                  label: c.financialAccount,
                                  filteringTags: [
                                    item.label,
                                    `GL ${item.gl}`,
                                    String(item.gl),
                                  ],
                                })),
                            }))}
                            placeholder="Choose a financial account"
                            statusType={
                              discovered.isLoading
                                ? "loading"
                                : discovered.isError
                                ? "error"
                                : "finished"
                            }
                            loadingText="Loading financial accounts"
                            errorText="Accounts could not be loaded"
                            recoveryText="Retry"
                            onLoadItems={() => {
                              if (discovered.isError)
                                discovered.refetch().catch(() => undefined);
                            }}
                            onChange={({ detail: change }) => {
                              const [p, a] = (
                                change.selectedOption.value || ""
                              ).split(":");
                              navigateTo(omsContextPath(p, a));
                            }}
                          />
                        </FormField>
                      </div>
                    </div>
                  </div>
                  {product && (
                    <nav
                      className="oms-context-navigation"
                      aria-label="OMS pages"
                    >
                      <Link
                        className="oms-context-navigation__item"
                        aria-current={
                          current?.view === "dashboard" ? "page" : undefined
                        }
                        to={omsContextPath(product.value, current?.account)}
                      >
                        Dashboard
                      </Link>
                      <Link
                        className="oms-context-navigation__item"
                        aria-current={
                          current?.view === "export" ? "page" : undefined
                        }
                        to={`${omsContextPath(
                          product.value,
                          current?.account
                        )}/export`}
                      >
                        Export
                      </Link>
                      <Link
                        className="oms-context-navigation__item"
                        aria-current={
                          current?.view === "manage" ||
                          current?.view === "edit" ||
                          current?.view === "copy"
                            ? "page"
                            : undefined
                        }
                        to={`${omsContextPath(
                          product.value,
                          current?.account
                        )}/presets`}
                      >
                        Presets
                      </Link>
                    </nav>
                  )}
                </SpaceBetween>
              }
            >
              {!current ? (
                <Alert type="error" header="Unsupported OMS context">
                  Check the product and financial account in this link.
                </Alert>
              ) : !product ? (
                <Box
                  padding="l"
                  color="text-status-inactive"
                  textAlign="center"
                >
                  Choose a financial account to open its dashboard and reports.
                </Box>
              ) : detail.isLoading ? (
                <div role="status" className="oms-report-download__status">
                  <Spinner />
                  <span>Loading financial account…</span>
                </div>
              ) : detail.isError || !context ? (
                <Alert
                  type="error"
                  header="Context unavailable"
                  action={
                    <Button
                      onClick={() => {
                        detail.refetch().catch(() => undefined);
                      }}
                    >
                      Retry
                    </Button>
                  }
                >
                  This link could not be resolved. No other account has been
                  substituted.
                </Alert>
              ) : (
                <OmsContextIdentityContext.Provider value={contextIdentity}>
                  <OMSContextContent
                    key={contextIdentity.namespace}
                    product={product.value}
                    label={product.label}
                    account={current.account}
                    context={context}
                    view={current.view}
                    presetId={current.presetId}
                    search={committed.search}
                    navigate={navigateTo}
                    onDirty={setDirty}
                    onNotify={onNotify}
                    onBeginExport={clearNotification}
                  />
                </OmsContextIdentityContext.Provider>
              )}
            </ContentLayout>
          }
        />
      </StageContext.Provider>
    </OMSHelpContext.Provider>
  );
}
